import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { findIndex, map, forEach } from 'lodash'

import { AppThunk } from '../store'
import { getMemberList } from '../../apis/member'
import { setErrors } from '../common/errorsSlice'
import {
  Member,
  GetMemberListResponse,
} from '../../types/member'

interface MemberListState {
  members: Member[]
  isLoading: boolean
  leftCount: number
  latest_versions: {
    macos: string
    windows: string
  }
  limit_member_number: number
}

const initialState: MemberListState = {
  members: [],
  isLoading: true,
  leftCount: 0,
  latest_versions: {
    macos: "",
    windows: "",
  },
  limit_member_number: 0,
}

const memberListSlice = createSlice({
  name: 'memberList',
  initialState,
  reducers: {
    requestStart: (state) => {
      state.isLoading = true
      return state
    },
    getMemberSuccess: (state, action: PayloadAction<GetMemberListResponse>) => {
      state.members = action.payload.member_list
      state.leftCount = action.payload.left_count
      state.latest_versions.macos = action.payload.latest_versions.macos
      state.latest_versions.windows = action.payload.latest_versions.windows
      state.limit_member_number = action.payload.limit_member_number
      state.isLoading = false
      return state
    },
    requestFailure: (state) => {
      state.isLoading = false
      return state
    },
    addListMember: (state, action: PayloadAction<Member>) => {
      state.members.push(action.payload)
      state.leftCount -= 1
      return state
    },
    addListMultiMembers: (state, action: PayloadAction<Member[]>) => {
      const ExistMemberIds = map(state.members, 'id')
      forEach(action.payload, (member: Member) => {
        if (ExistMemberIds.includes(member.id)) {
          const index = findIndex(state.members, { id: member.id })
          state.members[index] = member
        } else {
          state.members.push(member)
          state.leftCount -= 1
        }
      })
      return state
    },
    updateListMember: (state, action: PayloadAction<Member>) => {
      const index = findIndex(state.members, { id: action.payload.id })
      const editedMember = {
        ...state.members[index],
        ...action.payload,
      }
      state.members[index] = editedMember
      return state
    },
    deleteListMember: (state, action: PayloadAction<number>) => {
      state.members = state.members.filter((member) => member.id !== action.payload)
      state.leftCount += 1
      return state
    },
    resetMember: () => initialState,
  },
})

export const {
  requestStart,
  getMemberSuccess,
  requestFailure,
  addListMember,
  updateListMember,
  deleteListMember,
  addListMultiMembers,
  resetMember,
} = memberListSlice.actions

export const memberListReducer = memberListSlice.reducer

export const fetchMemberList = (
  callback?: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getMemberList()
    .then((result) => {
      dispatch(getMemberSuccess(result))
      if (callback) callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}
